// Risk Service
// Complete CRUD operations for Protocol Risks

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { calculateRPN, getCriticalityFromRPN } from '../types/risk';
import auditService from './auditService';

export class RiskService {
  constructor() {
    this.risksCollection = collection(db, 'protocol_risks');
  }
  
  /**
   * Create a new risk association
   * Now includes versioning support
   */
  async createRisk(riskData, userId) {
    const now = Timestamp.now();
    
    // Calculate RPN
    const rpn = calculateRPN(riskData.severity, riskData.occurrence, riskData.detection);
    
    // Auto-assign criticality based on RPN
    const criticality = getCriticalityFromRPN(rpn);
    
    // Initialize version 1.0
    const initialVersion = {
      version: '1.0',
      severity: riskData.severity,
      occurrence: riskData.occurrence,
      detection: riskData.detection,
      rpn: rpn,
      criticality: criticality,
      createdAt: now,
      createdBy: userId,
      source: riskData.source || 'Initial creation',
      changes: riskData.changeSummary || 'Initial version'
    };
    
    const risk = {
      ...riskData,
      rpn,
      criticality,
      status: 'Active',
      currentVersion: '1.0',
      versions: [initialVersion],
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    
    // Remove id field if it exists (it will be auto-generated by Firestore)
    delete risk.id;
    
    const docRef = await addDoc(this.risksCollection, risk);
    
    // Log audit trail
    await this.logRiskAction(docRef.id, userId, 'RISK_CREATED', {
      version: '1.0',
      rpn: rpn,
      criticality: criticality
    });
    try {
      auditService.logCreate('protocol_risks', docRef.id, riskData.riskScenario || riskData.name || 'Risk', { rpn, criticality });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
    return docRef.id;
  }
  
  /**
   * Get risk by ID
   */
  async getRisk(riskId) {
    const docRef = doc(this.risksCollection, riskId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }
  
  /**
   * Update risk - creates new version if RPN values change
   */
  async updateRisk(riskId, updates, userId, changeSummary = '') {
    const docRef = doc(this.risksCollection, riskId);
    const current = await this.getRisk(riskId);
    if (!current) throw new Error('Risk not found');
    
    const now = Timestamp.now();
    
    // Check if S, O, or D changed (triggers versioning)
    const sChanged = updates.severity !== undefined && updates.severity !== current.severity;
    const oChanged = updates.occurrence !== undefined && updates.occurrence !== current.occurrence;
    const dChanged = updates.detection !== undefined && updates.detection !== current.detection;
    
    let rpn = updates.rpn;
    let criticality = updates.criticality;
    let newVersion = null;
    
    if (sChanged || oChanged || dChanged) {
      // Calculate new RPN values
      const s = updates.severity ?? current.severity;
      const o = updates.occurrence ?? current.occurrence;
      const d = updates.detection ?? current.detection;
      rpn = calculateRPN(s, o, d);
      criticality = getCriticalityFromRPN(rpn);
      
      // Create new version
      const currentVersionNum = parseFloat(current.currentVersion || '1.0');
      const nextVersion = (currentVersionNum + 0.1).toFixed(1);
      
      newVersion = {
        version: nextVersion,
        severity: s,
        occurrence: o,
        detection: d,
        rpn: rpn,
        criticality: criticality,
        createdAt: now,
        createdBy: userId,
        source: updates.source || current.source || 'Risk update',
        changes: changeSummary || `Updated ${[sChanged && 'severity', oChanged && 'occurrence', dChanged && 'detection'].filter(Boolean).join(', ')}`
      };
      
      // Add to versions array
      const versions = [...(current.versions || []), newVersion];
      
      // Update risk with new version
      await updateDoc(docRef, {
        ...updates,
        rpn,
        criticality,
        currentVersion: nextVersion,
        versions: versions,
        updatedAt: now,
        updatedBy: userId
      });
      
      // Notify all protocol associations using this risk
      await this.notifyRiskUpdate(riskId, current.currentVersion || '1.0', nextVersion);
      
      // Log audit trail
      await this.logRiskAction(riskId, userId, 'RISK_VERSION_CREATED', {
        fromVersion: current.currentVersion || '1.0',
        toVersion: nextVersion,
        oldRPN: current.rpn,
        newRPN: rpn,
        changeSummary: changeSummary
      });
    } else {
      // No version change, just update metadata
      await updateDoc(docRef, {
        ...updates,
        rpn: rpn || current.rpn,
        criticality: criticality || current.criticality,
        updatedAt: now,
        updatedBy: userId
      });
      
      // Log audit trail
      await this.logRiskAction(riskId, userId, 'RISK_UPDATED', {
        version: current.currentVersion || '1.0',
        changedFields: Object.keys(updates).filter(k => k !== 'rpn' && k !== 'criticality')
      });
    }
    try {
      auditService.logUpdate('protocol_risks', riskId, current.riskScenario || current.name || 'Risk', { rpn: rpn || current.rpn });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }
  
  /**
   * Get all risks for a standard (process) - QMS Phase 2
   */
  async getProcessRisks(processId) {
    if (!processId) return [];
    const q = query(
      this.risksCollection,
      where('processId', '==', processId),
      orderBy('rpn', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      delete data.id;
      return { id: doc.id, ...data };
    });
  }

  /**
   * Get all risks for a protocol
   */
  async getProtocolRisks(protocolId) {
    const q = query(
      this.risksCollection,
      where('protocolId', '==', protocolId),
      orderBy('rpn', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      delete data.id; // Ensure document ID takes precedence
      return {
        id: doc.id,
        ...data
      };
    });
  }
  
  /**
   * Get risks for a specific step
   */
  async getStepRisks(stepId) {
    const q = query(
      this.risksCollection,
      where('stepId', '==', stepId),
      orderBy('rpn', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      delete data.id; // Ensure document ID takes precedence
      return {
        id: doc.id,
        ...data
      };
    });
  }
  
  /**
   * Get risks for a specific action
   */
  async getActionRisks(actionId) {
    const q = query(
      this.risksCollection,
      where('actionId', '==', actionId),
      orderBy('rpn', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      delete data.id; // Ensure document ID takes precedence
      return {
        id: doc.id,
        ...data
      };
    });
  }
  
  /**
   * Get risks by biological entity
   */
  async getRisksByBiologicalEntity(entityId) {
    // Note: This requires a composite index on biological_entities array and rpn
    const allRisks = await getDocs(this.risksCollection);
    
    return allRisks.docs
      .map(doc => {
        const data = doc.data();
        delete data.id; // Ensure document ID takes precedence
        return { id: doc.id, ...data };
      })
      .filter(risk => 
        risk.biological_entities && risk.biological_entities.some(entity => entity.biologicalEntityId === entityId)
      )
      .sort((a, b) => b.rpn - a.rpn);
  }
  
  /**
   * Get high-risk associations (RPN > threshold)
   */
  async getHighRiskAssociations(rpnThreshold = 100) {
    const q = query(
      this.risksCollection,
      where('rpn', '>=', rpnThreshold),
      orderBy('rpn', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      delete data.id; // Ensure document ID takes precedence
      return {
        id: doc.id,
        ...data
      };
    });
  }
  
  /**
   * Add biological entity association to risk
   */
  async addBiologicalEntityAssociation(riskId, association, userId) {
    const risk = await this.getRisk(riskId);
    if (!risk) throw new Error('Risk not found');
    
    const updatedAssociations = [...(risk.biological_entities || []), association];
    
    await this.updateRisk(riskId, {
      biological_entities: updatedAssociations
    }, userId);
  }
  
  /**
   * Remove biological entity association
   */
  async removeBiologicalEntityAssociation(riskId, entityId, userId) {
    const risk = await this.getRisk(riskId);
    if (!risk) throw new Error('Risk not found');
    
    const updatedAssociations = (risk.biological_entities || []).filter(
      entity => entity.biologicalEntityId !== entityId
    );
    
    await this.updateRisk(riskId, {
      biological_entities: updatedAssociations
    }, userId);
  }
  
  /**
   * Calculate total RPN for a protocol
   */
  async calculateProtocolTotalRPN(protocolId) {
    const risks = await this.getProtocolRisks(protocolId);
    return risks.reduce((total, risk) => total + risk.rpn, 0);
  }
  
  /**
   * Calculate max RPN for a protocol (if all steps skipped)
   */
  async calculateProtocolMaxRPN(protocolId) {
    const risks = await this.getProtocolRisks(protocolId);
    
    // Sum only critical step/action risks
    return risks
      .filter(risk => risk.criticality === 'Critical' || risk.criticality === 'High')
      .reduce((total, risk) => total + risk.rpn, 0);
  }
  
  /**
   * Remove risk from protocol/step/action – unlink only. Risk stays in registry with description, thresholds, controls.
   */
  async removeFromProtocol(riskId, userId = 'system') {
    const docRef = doc(this.risksCollection, riskId);
    const current = await this.getRisk(riskId);
    if (!current) throw new Error('Risk not found');

    await updateDoc(docRef, {
      protocolId: null,
      protocolName: null,
      stepId: null,
      stepName: null,
      stepSequence: null,
      actionId: null,
      actionName: null,
      actionSequence: null,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    try {
      auditService.logUpdate('protocol_risks', riskId, current?.riskScenario || current?.name || 'Risk', { action: 'removed_from_protocol' });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }

  /**
   * Delete risk (permanent – use only when removing from risk registry, not when unlinking from protocol)
   * Now includes dependency checking - throws error if dependencies exist
   */
  async deleteRisk(riskId, userId = 'system', force = false) {
    // Check dependencies before deletion
    if (!force) {
      const { dependencyCheckService } = await import('./dependencyCheckService');
      const dependencyCheck = await dependencyCheckService.checkRiskDeletion(riskId);
      
      if (!dependencyCheck.canDelete && dependencyCheck.blockingDependencies.length > 0) {
        const errorMessages = dependencyCheck.blockingDependencies.map(d => d.message).join('; ');
        throw new Error(`Cannot delete risk: ${errorMessages}`);
      }
    }

    const docRef = doc(this.risksCollection, riskId);
    const current = await this.getRisk(riskId);
    
    // Log audit trail
    await this.logRiskAction(riskId, userId, 'RISK_DELETED', {
      deletedAt: Timestamp.now()
    });
    try {
      auditService.logDelete('protocol_risks', riskId, current?.riskScenario || current?.name || 'Risk', {});
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
    
    await deleteDoc(docRef);
  }
  
  /**
   * Get all risks (for admin/overview)
   */
  async getAllRisks() {
    const q = query(
      this.risksCollection,
      orderBy('rpn', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      // Ensure document ID takes precedence over any id field in the data
      delete data.id;
      return {
        id: doc.id,
        ...data
      };
    });
  }
  
  /**
   * Get risks by criticality level
   */
  async getRisksByCriticality(criticality) {
    const q = query(
      this.risksCollection,
      where('criticality', '==', criticality),
      orderBy('rpn', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      delete data.id; // Ensure document ID takes precedence
      return {
        id: doc.id,
        ...data
      };
    });
  }
  
  /**
   * Create risk association (link existing risk to protocol/step/action)
   * This is used when selecting a risk from the library
   */
  async createRiskAssociation(associationData) {
    const now = Timestamp.now();
    
    // Get the risk to get its current version
    const risk = await this.getRisk(associationData.riskId);
    if (!risk) throw new Error('Risk not found');
    
    const association = {
      ...associationData,
      riskVersion: risk.currentVersion || '1.0', // Store which version is associated
      associatedAt: now,
      associatedBy: associationData.associatedBy || 'current-user', // TODO: Pass userId
      lastReviewedAt: now,
      updateNotifications: []
    };
    
    const docRef = await addDoc(this.risksCollection, association);
    
    // Log audit trail
    await this.logRiskAction(associationData.riskId, associationData.associatedBy || 'current-user', 'RISK_ASSOCIATED', {
      associationId: docRef.id,
      protocolId: associationData.protocolId,
      stepId: associationData.stepId,
      actionId: associationData.actionId,
      riskVersion: risk.currentVersion || '1.0'
    });
    
    console.log(`✅ Created risk association: ${docRef.id}`);
    return docRef.id;
  }
  
  /**
   * Notify all protocol associations when a risk is updated
   */
  async notifyRiskUpdate(riskId, oldVersion, newVersion) {
    // Find all associations using this risk with the old version
    const q = query(
      this.risksCollection,
      where('riskId', '==', riskId),
      where('riskVersion', '==', oldVersion)
    );
    
    const snapshot = await getDocs(q);
    const now = Timestamp.now();
    
    // Update each association with a notification
    for (const docSnap of snapshot.docs) {
      const association = docSnap.data();
      const notifications = association.updateNotifications || [];
      
      // Add new notification
      notifications.push({
        fromVersion: oldVersion,
        toVersion: newVersion,
        notifiedAt: now,
        acknowledged: false,
        acknowledgedBy: null,
        acknowledgedAt: null,
        action: null // 'upgraded' | 'kept_old' | 'dismissed'
      });
      
      await updateDoc(doc(docSnap.ref), {
        updateNotifications: notifications
      });
    }
  }
  
  /**
   * Upgrade protocol association to new risk version
   */
  async upgradeAssociationToVersion(associationId, newVersion, userId) {
    const associationRef = doc(this.risksCollection, associationId);
    const associationSnap = await getDoc(associationRef);
    
    if (!associationSnap.exists()) throw new Error('Association not found');
    
    const association = associationSnap.data();
    const now = Timestamp.now();
    
    // Update the notification
    const notifications = (association.updateNotifications || []).map(notif => {
      if (notif.toVersion === newVersion && !notif.acknowledged) {
        return {
          ...notif,
          acknowledged: true,
          acknowledgedBy: userId,
          acknowledgedAt: now,
          action: 'upgraded'
        };
      }
      return notif;
    });
    
    // Get the risk to ensure version exists
    const risk = await this.getRisk(association.riskId);
    if (!risk) throw new Error('Risk not found');
    
    const versionExists = risk.versions?.some(v => v.version === newVersion);
    if (!versionExists) throw new Error(`Risk version ${newVersion} does not exist`);
    
    await updateDoc(associationRef, {
      riskVersion: newVersion,
      updateNotifications: notifications,
      lastReviewedAt: now
    });
    
    // Log audit trail
    await this.logRiskAction(association.riskId, userId, 'ASSOCIATION_UPGRADED', {
      associationId: associationId,
      fromVersion: association.riskVersion,
      toVersion: newVersion,
      protocolId: association.protocolId
    });
  }
  
  /**
   * Keep old version (dismiss upgrade notification)
   */
  async keepOldVersion(associationId, newVersion, userId, reason = '') {
    const associationRef = doc(this.risksCollection, associationId);
    const associationSnap = await getDoc(associationRef);
    
    if (!associationSnap.exists()) throw new Error('Association not found');
    
    const association = associationSnap.data();
    const now = Timestamp.now();
    
    // Update the notification
    const notifications = (association.updateNotifications || []).map(notif => {
      if (notif.toVersion === newVersion && !notif.acknowledged) {
        return {
          ...notif,
          acknowledged: true,
          acknowledgedBy: userId,
          acknowledgedAt: now,
          action: 'kept_old',
          reason: reason
        };
      }
      return notif;
    });
    
    await updateDoc(associationRef, {
      updateNotifications: notifications,
      lastReviewedAt: now
    });
    
    // Log audit trail
    await this.logRiskAction(association.riskId, userId, 'ASSOCIATION_KEPT_OLD_VERSION', {
      associationId: associationId,
      version: newVersion,
      protocolId: association.protocolId,
      reason: reason
    });
  }
  
  /**
   * Get risk version history
   */
  async getRiskVersionHistory(riskId) {
    const risk = await this.getRisk(riskId);
    if (!risk) throw new Error('Risk not found');
    
    return risk.versions || [];
  }
  
  /**
   * Get associations with pending notifications
   */
  async getAssociationsWithPendingNotifications(protocolId = null) {
    const q = protocolId
      ? query(
          this.risksCollection,
          where('protocolId', '==', protocolId)
        )
      : query(this.risksCollection);
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs
      .map(doc => {
        const data = doc.data();
        delete data.id;
        return { id: doc.id, ...data };
      })
      .filter(association => {
        const notifications = association.updateNotifications || [];
        return notifications.some(n => !n.acknowledged);
      });
  }
  
  /**
   * Log risk action for audit trail
   */
  async logRiskAction(riskId, userId, actionType, metadata = {}) {
    try {
      const auditLogsCollection = collection(db, 'risk_audit_logs');
      const now = Timestamp.now();
      
      await addDoc(auditLogsCollection, {
        riskId: riskId,
        userId: userId,
        actionType: actionType,
        timestamp: now,
        metadata: metadata
      });
    } catch (error) {
      // Don't fail the main operation if audit logging fails
      console.error('Error logging risk action:', error);
    }
  }
  
  /**
   * Get audit trail for a risk
   */
  async getRiskAuditTrail(riskId) {
    try {
      const auditLogsCollection = collection(db, 'risk_audit_logs');
      const q = query(
        auditLogsCollection,
        where('riskId', '==', riskId),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      return [];
    }
  }

  /**
   * FMEA: flag O-score review when CAPA closes (PRD_FMEA_OScore_Review_v1).
   * Does not modify deprecated fields capaOScoreReviewRequestedAt / lastLinkedCapaId.
   * @param {string} riskId
   * @param {string|null} capaId
   */
  async flagOScoreReview(riskId, capaId) {
    if (!riskId) return;
    try {
      const ref = doc(this.risksCollection, riskId);
      await updateDoc(ref, {
        oScoreReviewPending: true,
        oScoreReviewCapaId: capaId || null,
        oScoreReviewFlaggedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      console.log(`[riskService] O score review flagged for risk ${riskId} (CAPA: ${capaId})`);
    } catch (e) {
      console.warn('[riskService] flagOScoreReview failed:', e?.message || e);
    }
  }

  /**
   * Mark O-score review complete after IPC/management confirms review (PRD_FMEA_OScore_Review_v1).
   * @param {string} riskId
   * @param {string} userId
   */
  async completeOScoreReview(riskId, userId) {
    if (!riskId) return;
    const ref = doc(this.risksCollection, riskId);
    await updateDoc(ref, {
      oScoreReviewPending: false,
      oScoreReviewCompletedAt: Timestamp.now(),
      oScoreReviewCompletedBy: userId,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    try {
      await auditService.logUpdate(
        'protocol_risks',
        riskId,
        'O Score review completed',
        { action: 'complete_o_score_review' }
      );
    } catch (e) {
      console.warn('[riskService] audit log completeOScoreReview failed:', e);
    }
  }
}

export const riskService = new RiskService();
